import { ART_PALETTE } from '../art-palette.js';
import { repairAppearance } from './appearance.js';
import {
  CLOTH_AUTHOR,
  CLOTH_COLOR_BY_ID,
  DIRECTIONS,
  FRAME_HEIGHT,
  FRAME_WIDTH,
  HAIR_AUTHOR,
  HAIR_COLOR_BY_ID,
  HAIR_STYLE_BY_ID,
  SHEET_HEIGHT,
  SHEET_WIDTH,
  SKIN_AUTHOR,
  SKIN_BY_ID,
  WALK_FRAMES,
  remapTable,
} from './catalog.js';
import { contactShadow, outerContour } from '../pixel-kit.js';
import {
  BODY_PARTS,
  BOTTOM_PARTS,
  HAIR_BACK_PARTS,
  HAIR_FRONT_PARTS,
  TOP_PARTS,
} from './parts/index.js';

function parseColor(value) {
  return [1, 3, 5, 7].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
}

const PALETTE_RGBA = ART_PALETTE.map(parseColor);

function blankSheet() {
  return Array.from({ length: SHEET_HEIGHT }, () => Array(SHEET_WIDTH).fill(0));
}

function blit(dest, src, destX, destY, remap) {
  for (let y = 0; y < FRAME_HEIGHT; y++) {
    for (let x = 0; x < FRAME_WIDTH; x++) {
      const index = src[y][x];
      if (!index) continue;
      dest[destY + y][destX + x] = remap[index] ?? index;
    }
  }
}

export function appearanceLayers(appearance) {
  const look = repairAppearance(appearance);
  const skinMap = remapTable(SKIN_AUTHOR, SKIN_BY_ID.get(look.skin).ramp);
  const hairMap = remapTable(HAIR_AUTHOR, HAIR_COLOR_BY_ID.get(look.hairColor).ramp);
  const topMap = remapTable(CLOTH_AUTHOR, CLOTH_COLOR_BY_ID.get(look.topColor).ramp);
  const bottomMap = remapTable(CLOTH_AUTHOR, CLOTH_COLOR_BY_ID.get(look.bottomColor).ramp);
  const layers = [];
  if (HAIR_STYLE_BY_ID.get(look.hairStyle).hasBack) {
    layers.push({ pixels: HAIR_BACK_PARTS[look.hairStyle], remap: hairMap });
  }
  layers.push({ pixels: BODY_PARTS[look.gender], remap: skinMap });
  layers.push({ pixels: BOTTOM_PARTS[look.bottom], remap: bottomMap });
  layers.push({ pixels: TOP_PARTS[look.top], remap: topMap });
  layers.push({ pixels: HAIR_FRONT_PARTS[look.hairStyle], remap: hairMap });
  return layers;
}

export function composeAppearance(appearance) {
  const layers = appearanceLayers(appearance);
  const sheet = blankSheet();
  for (let d = 0; d < DIRECTIONS.length; d++) {
    for (let f = 0; f < WALK_FRAMES; f++) {
      const destX = f * FRAME_WIDTH;
      const destY = d * FRAME_HEIGHT;
      for (const { pixels, remap } of layers) blit(sheet, pixels[d][f], destX, destY, remap);
      // one Ink ring around the composed silhouette (no per-layer seams),
      // then the contact shadow into whatever transparency remains
      outerContour(sheet, {
        x0: destX, y0: destY,
        x1: destX + FRAME_WIDTH - 1, y1: destY + FRAME_HEIGHT - 1,
      });
      contactShadow(sheet, destX + 7.5, destY + 22.6, 5.8, 1.6);
    }
  }
  return sheet;
}

export function sheetToRgba(sheet) {
  const height = sheet.length;
  const width = sheet[0].length;
  const rgba = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const color = PALETTE_RGBA[sheet[y][x]] ?? PALETTE_RGBA[0];
      const i = (y * width + x) * 4;
      rgba[i] = color[0];
      rgba[i + 1] = color[1];
      rgba[i + 2] = color[2];
      rgba[i + 3] = color[3];
    }
  }
  return { width, height, rgba };
}

export function sheetToCanvas(sheet, documentRef = globalThis.document) {
  const { width, height, rgba } = sheetToRgba(sheet);
  const canvas = documentRef.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const imageData = ctx.createImageData(width, height);
  imageData.data.set(rgba);
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

export { DIRECTIONS, FRAME_HEIGHT, FRAME_WIDTH, SHEET_HEIGHT, SHEET_WIDTH, WALK_FRAMES };
